import api from './api';
import { searchText } from '../utils/koreanUtils';
import DESIGN_TEMPLATE_ID from '../config/designTemplate';
import { markGameSessionOpen, markGameSessionClosed } from '../utils/sessionGuard';

// 게임 관련 API 서비스
// [2026-11-03] 게임 목록 전용 15초 제한을 없앴다(공용 한도 60초). WHY: 응답이 15초 넘게 밀리면 서버가 이미 보낸 목록도 버리고 실패 알림을 띄웠다
//   (2026-11-03 14:19 KST ur1 실측: 서버는 1초 안에 200 응답, 브라우저는 15초에 포기). 창이 먼저 열려 기다리는 동안 안내가 보이므로 끊을 이유가 없다.

// 게임 목록 실패를 서버 기록에 남긴다. WHY: 서버까지 요청이 안 온 실패는 서버 쪽에 흔적이 없어 원인을 가를 수 없다(2026-11-01 ur3 3회 중 2회).
//   로그인 만료(401)는 services/api.js 가 처리하므로 남기지 않는다. 기록 실패는 화면에 영향이 없게 무시한다.
const reportGameListFailure = (params, error, elapsedMs) => {
  const status = error?.response?.status;
  if (status === 401) return;
  const reason = status ? `http_${status}` : (error?.code || 'no_response');
  api.post('/games/list-failure', { vendor: params.vendor, reason, elapsedMs }).catch(() => {});
};

// 게임 목록을 못 받았을 때의 회원 안내 문구 — 서버 문구와 연결 문구(services/api.js)를 먼저 쓴다
export const gameListErrorMessage = (error) => {
  const fallback = '게임 목록을 불러오는데 실패했습니다.';
  if (typeof error?.error === 'string' && error.error) return error.error;
  return error?.response ? fallback : (error?.message || fallback);
};

const gamesService = {
  // 게임 목록 조회
  async getGameList(params = {}) {
    const startedAt = Date.now();
    try {
      // limit 파라미터가 없으면 기본값 1000 설정
      if (!params.limit) {
        params.limit = 1000;
      }
      const response = await api.get('/games/list', { params });
      // API 응답 구조 정규화 - data 또는 games 배열 처리
      if (response.data) {
        if (response.data.data) {
          return { games: response.data.data };
        } else if (response.data.games) {
          return response.data;
        }
      }
      return response.data;
    } catch (error) {
      reportGameListFailure(params, error, Date.now() - startedAt);
      throw error.response?.data || error;
    }
  },

  // 게임 검색 (개선된 버전)
  async searchGames(keyword, type = null) {
    try {
      const params = { 
        keyword,
        limit: 10 // 검색 결과 수 제한
      };
      if (type) params.type = type;
      
      const response = await api.get('/games/search', { params });
      
      // 빈 결과인 경우 fallback 로직 실행
      if (response.data && (response.data.games?.length === 0 || response.data.results?.length === 0)) {
        throw new Error('Empty search results');
      }
      
      return response.data;
    } catch (error) {
      // 검색 API가 없을 경우 게임 목록에서 필터링
      console.log('검색 API 실패, fallback 로직 실행');
      try {
        const allGamesResponse = await api.get('/games/list', { 
          params: { 
            limit: 1000 // 더 많은 게임을 가져와서 검색
          } 
        });
        
        const gamesData = allGamesResponse.data?.data || allGamesResponse.data?.games || [];
        console.log('전체 게임 데이터:', gamesData.length, '개');
        
        if (gamesData.length > 0) {
          const filteredGames = gamesData.filter(game => {
            // 주요 검색 대상: 게임 이름 (한글/영문)
            if (game.game_name_ko && searchText(game.game_name_ko, keyword)) {
              return true;
            }
            if (game.game_name && searchText(game.game_name, keyword)) {
              return true;
            }
            
            // 부가 검색 대상: 제공사 이름 (정확도를 위해 제한적으로)
            // 키워드가 3글자 이상일 때만 제공사 검색
            if (keyword.length >= 3) {
              const providerName = game.vendor || game.provider || game.category_name;
              const providerNameKo = game.category_name_ko;
              
              if (providerName && searchText(providerName, keyword)) {
                return true;
              }
              if (providerNameKo && searchText(providerNameKo, keyword)) {
                return true;
              }
            }
            
            return false;
          });
          
          // 검색 결과 정렬 (관련성 순)
          const sortedGames = filteredGames.sort((a, b) => {
            // 한글 이름이 정확히 일치하면 최우선
            const aExactKo = a.game_name_ko?.toLowerCase() === keyword.toLowerCase();
            const bExactKo = b.game_name_ko?.toLowerCase() === keyword.toLowerCase();
            if (aExactKo && !bExactKo) return -1;
            if (!aExactKo && bExactKo) return 1;
            
            // 영문 이름이 정확히 일치하면 다음 우선
            const aExactEn = a.game_name?.toLowerCase() === keyword.toLowerCase();
            const bExactEn = b.game_name?.toLowerCase() === keyword.toLowerCase();
            if (aExactEn && !bExactEn) return -1;
            if (!aExactEn && bExactEn) return 1;
            
            // 시작 문자열 일치 여부
            const aStartsKo = a.game_name_ko?.toLowerCase().startsWith(keyword.toLowerCase());
            const bStartsKo = b.game_name_ko?.toLowerCase().startsWith(keyword.toLowerCase());
            if (aStartsKo && !bStartsKo) return -1;
            if (!aStartsKo && bStartsKo) return 1;
            
            return 0;
          });
          
          console.log('필터링된 게임:', sortedGames.length, '개');
          return { games: sortedGames.slice(0, 10) }; // 최대 10개만 반환
        }
        
        return { games: [] }; // 빈 배열 반환
      } catch (fallbackError) {
        console.error('Fallback 로직 에러:', fallbackError);
        return { games: [] }; // 에러 시에도 빈 배열 반환
      }
    }
  },

  // 게임사 검색 (새로 추가)
  async searchProviders(keyword) {
    try {
      // 게임사 검색 API가 없으므로 바로 전체 목록에서 필터링
      const providersResponse = await this.getProviders();
      
      if (providersResponse && providersResponse.providers) {
        const filteredProviders = providersResponse.providers.filter(provider => {
          // 한글 이름 우선 검색
          if (provider.name_ko && searchText(provider.name_ko, keyword)) {
            return true;
          }
          
          // 영문 이름 검색
          if (provider.name && searchText(provider.name, keyword)) {
            return true;
          }
          
          // provider ID 검색 (정확히 일치하는 경우만)
          if (provider.provider && provider.provider.toLowerCase() === keyword.toLowerCase()) {
            return true;
          }
          
          return false;
        });
        
        return { providers: filteredProviders };
      }
      
      throw new Error('No providers found');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // 인기 게임 조회
  async getPopularGames(limit = 10) {
    try {
      const response = await api.get('/games/popular', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // 신규 게임 조회
  async getNewGames(limit = 10) {
    try {
      const response = await api.get('/games/new', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // 추천 게임 조회
  async getRecommendedGames(limit = 10) {
    try {
      const response = await api.get('/games/recommended', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // 게임 제공사 목록 (개선된 버전)
  async getProviders(gameType = null, apiType = null) {
    try {
      const params = {};
      // [2026-06-10 단계2] 이 빌드(디자인)의 템플릿 ID를 함께 보내 디자인별 게임사 자산을 응답에 포함시킨다.
      //   값이 비어 있으면 보내지 않고, 서버도 template_id 미전송 시 기존 응답 그대로(무손상).
      if (DESIGN_TEMPLATE_ID) {
        params.template_id = DESIGN_TEMPLATE_ID;
      }
      if (gameType) {
        params.game_type = gameType;
      }
      if (apiType) {
        params.api_type = apiType;
      }
      const response = await api.get('/providers', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // 게임 상세 정보 조회
  async getGame(gameId) {
    try {
      const response = await api.get(`/games/${gameId}`);
      console.log('[Games Service] Get game response:', response.data);
      
      // Backend returns data in response.data.data format
      if (response.data && response.data.success && response.data.data) {
        return { game: response.data.data };
      }
      
      // Fallback for game field
      if (response.data && response.data.game) {
        return response.data;
      }
      
      // If no proper game data found
      console.error('[Games Service] No game data in response:', response.data);
      throw new Error('게임 정보를 찾을 수 없습니다.');
    } catch (error) {
      console.error('[Games Service] Get game error:', error);
      throw error.response?.data || error;
    }
  },

  // 게임 실행
  async launchGame(gameId) {
    try {
      const response = await api.post(`/games/${gameId}/launch`);
      console.log('[Games Service] Launch response:', response.data);
      
      // Backend returns launch_url in response.data.launch_url
      if (response.data && response.data.success && response.data.launch_url) {
        // [2026-09-07] 게임이 열렸다 → 무활동 자동 로그아웃 타이머를 멈춘다(정본 SPEC §3).
        markGameSessionOpen();
        return { 
          success: true, 
          gameUrl: response.data.launch_url,
          game_info: response.data.game_info 
        };
      }
      
      // Fallback for different response formats
      if (response.data && response.data.success && response.data.gameUrl) {
        // [2026-09-07] 위와 같은 이유 — 응답 형식만 다르고 게임이 열린 것은 같다.
        markGameSessionOpen();
        return response.data;
      }
      
      // If no proper URL found
      console.error('[Games Service] No game URL in response:', response.data);
      throw new Error('게임 URL을 받지 못했습니다.');
    } catch (error) {
      console.error('[Games Service] Launch error:', error);
      throw error.response?.data || error;
    }
  },

  /* 게임 종료 알림
     [2026-08-21] confirmed 로 신호의 성격을 함께 보낸다.
       true  = 게임이 실제로 끝난 것이 확인된 경우 — 게임 창이 닫힌 것을 확인했거나,
               화면 안 덧창을 닫았거나, 덧창을 켠 채 화면을 벗어난 경우(덧창은 화면과 함께 사라진다).
       false = 끝났는지 확신할 수 없는 경우 — 게임 창은 살아 있는데 우리 화면만 벗어난 경우.
     WHY: 두 가지를 같은 통보로 보내면, 새로고침이나 메뉴 이동만으로도 게임이 끝난 것으로
       처리돼 실제로 게임 중인 회원의 접속상태가 '온라인'으로 바뀌었다(실측). */
  async endGame(confirmed = false) {
    /* [2026-09-07] 게임이 끝난 것이 확인된 신호일 때만 무활동 타이머를 다시 돌린다.
       WHY: confirmed=false 는 게임 창이 살아 있는데 우리 화면만 벗어난 경우라,
         여기서 풀면 실제로 게임 중인 회원이 무활동으로 로그아웃된다. */
    if (confirmed === true) {
      markGameSessionClosed();
    }
    try {
      const response = await api.post('/game/end-session', { confirmed });
      return response.data;
    } catch (error) {
      // 게임 종료는 에러가 발생해도 사용자에게 영향이 없도록 조용히 처리
      console.error('게임 종료 처리 중 오류:', error);
      return { success: false, error: error.response?.data || error };
    }
  },

  // 벤더 로비 실행 (카지노 로비 전용)
  async launchVendorLobby(vendor) {
    try {
      // [2026-06-02] 벤더 표준코드에 공백이 있을 수 있어(예: 'NOLIMIT CITY','PG SOFT') 경로 인코딩 필수.
      //   공백 없는 기존 카지노 코드(EVOLUTION 등)는 인코딩 결과 불변이라 회귀 없음. 백엔드는 :vendor 자동 디코드.
      const response = await api.post(`/games/vendor/${encodeURIComponent(vendor)}/lobby`);
      console.log('[Games Service] Vendor lobby launch response:', response.data);

      if (response.data && response.data.success) {
        // Backend returns lobbyUrl directly (not nested in data.data)
        const lobbyUrl = response.data.lobbyUrl || response.data.data?.url;

        if (!lobbyUrl) {
          console.error('[Games Service] No lobby URL in response:', response.data);
          throw new Error('로비 URL을 받지 못했습니다.');
        }

        // [2026-09-07] 로비(새 창)도 게임 세션이다 → 무활동 타이머 정지.
        markGameSessionOpen();
        return {
          success: true,
          lobbyUrl: lobbyUrl,
          gameUrl: lobbyUrl,  // Compatibility
          vendor: response.data.vendor || vendor,
          fallback: response.data.fallback || false
        };
      }

      console.error('[Games Service] Invalid response format:', response.data);
      throw new Error('로비 URL을 받지 못했습니다.');
    } catch (error) {
      console.error('[Games Service] Vendor lobby launch error:', error);
      throw error.response?.data || error;
    }
  }
};

export default gamesService;
